import { useContext, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"

// ** Theme Components
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"

// ** Routes
import { SERVER_URL, STATUS_DICTIONARY_ENDPOINT } from "@pma/utils/api-routes"

// ** Variables & Constants
import { DictType } from "@pma/types"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"

// ** Custom Context
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** Helper
import { cn } from "@pma/utils/helpers"
import { useAuth } from "@stores/auth-store"
import { editTaskByCurrentUser } from "@pma/utils/utils"

// ** Custom Components
import AddStatusForm from "./add-status-form"

interface Props {
  status?: DictType
  taskId?: number
  setStatus?: any
  priorityId?: number
  parentTaskId?: number
}
const TaskStatusEditor = ({ status, taskId, setStatus, priorityId, parentTaskId }: Props) => {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParams] = useSearchParams()
  const { translate, locale } = useTranslator()

  const { setTaskExpandForLoading } = useContext(GroupedTasksContext)

  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParams)

  const [open, setOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(status)

  const getStatuses = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${STATUS_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}&for_task=true&for_project=false`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch } = useQuery({
    queryKey: ["task-statuses", orgId],
    queryFn: getStatuses,
  })

  const updateTaskStatusMutation = useMutation({
    mutationKey: ["update-task-status"],
    mutationFn: (statusId: number) =>
      editTaskByCurrentUser(taskId || 0, {
        status_id: statusId,
        inform_users: true,
        priority_id: priorityId,
      }),
    onSuccess: (data: DictType) => {
      // Trigger Revalidate tasks
      // queryClient.invalidateQueries({ queryKey: ['clear-selected-tasks'] })
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  if (taskId) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='default'
            role='combobox'
            aria-expanded={open}
            className='h-6 w-fit flex-col items-center rounded-full text-[11px] text-black md:flex md:min-w-[80px]'
            style={{
              background:
                selectedStatus?.color === "#transparent" ? undefined : selectedStatus?.color,
            }}
          >
            {selectedStatus ? selectedStatus?.name : "--"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[250px] p-0'>
          <Command className={isHebrew ? "rtl" : "ltr"}>
            <CommandInput placeholder={`${translate("Search")}...`} />
            <CommandList>
              <CommandEmpty>{translate("No status found")}.</CommandEmpty>
              <CommandGroup>
                {data?.map((item: DictType) => (
                  <CommandItem
                    key={item.id}
                    value={item?.name}
                    onSelect={(currentValue: any) => {
                      setOpen(false)
                      setSelectedStatus(item)
                      updateTaskStatusMutation.mutate(item?.id)
                    }}
                    className={cn(
                      "my-1 text-black",
                      item?.color === "#transparent" && "bg-primary",
                    )}
                    style={{ background: item?.color }}
                  >
                    {item.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className='p-2'>
            <AddStatusForm option='task' refetch={refetch} />
          </div>{" "}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='secondary'
          role='combobox'
          aria-expanded={open}
          className='h-8 w-full text-black'
          style={{ background: selectedStatus?.color }}
        >
          {selectedStatus ? selectedStatus?.name : translate("Select Status")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command className={isHebrew ? "rtl" : "ltr"}>
          <CommandInput placeholder={`${translate("Search")}...`} />
          <CommandList>
            <CommandEmpty>{translate("No status found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((item: DictType) => (
                <CommandItem
                  key={item.id}
                  value={item?.name}
                  onSelect={(currentValue: any) => {
                    setOpen(false)
                    setSelectedStatus(item)
                    setStatus && setStatus(item)
                  }}
                  className={cn("my-1 text-white", item?.color === "#transparent" && "bg-primary")}
                  style={{ background: item?.color }}
                >
                  {item.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className='p-2'>
          <AddStatusForm option='task' refetch={refetch} />
        </div>{" "}
      </PopoverContent>
    </Popover>
  )
}

export default TaskStatusEditor
